/**
 * Entity types
 * - To be replace by auto-generated types from schema
 */
/* eslint-disable import/no-extraneous-dependencies */
import { IContentMedium, IContentSchema } from '@nicejob-library/content-templates/types';
import { IAddress } from '@nicejob-library/data-platform-implementation/entities/common';
import { ICompany } from '@nicejob-library/data-platform-implementation/entities/Company';
import {
    CompanyProductPlan,
    CompanyProductPlanDate,
} from '@nicejob-library/data-platform-implementation/entities/Company/ProductStatus/types';
import { CompanyProductId } from '@nicejob-library/data-platform-implementation/entities/Company/types';
import { IGiftConfiguration } from '@nicejob-library/data-platform-implementation/entities/GiftConfiguration';
import { ISugarwishGiftConfigurationParameters } from '@nicejob-library/data-platform-implementation/entities/GiftConfiguration/GiftConfigurationParameters/SugarwishGiftConfigurationParameters';
import { IGiftIntent } from '@nicejob-library/data-platform-implementation/entities/GiftIntent';
import { IGiftTemplate } from '@nicejob-library/data-platform-implementation/entities/GiftTemplate';
import { ICalculateResponse } from '@nicejob-library/enrichment';
import { ThreatType } from '@nicejob-library/gcp-web-risk';
import { ILanguage } from '@nicejob-library/internationalization';
import { IPublishedNotification } from '@nicejob-library/publish-notification';
import { IPubsubMetadata } from '@nicejob-library/pubsub/types';
import { LDUser } from 'launchdarkly-js-client-sdk';
import { Scope } from '../common/SCOPES';
import { IAccountRole, ICompanyRole, IEntityKey, IUserRole } from './common.types';
import HttpStatusCode from './httpstatuscode.enum';

export type OptionalEntityBase<
    K extends IEntityBase<string> = IEntityBase<string>,
    F extends keyof K = 'key' | 'id' | 'updated_at' | 'created_at' | 'deleted' | 'deleted_at'
> = Omit<K, F> & Partial<Pick<K, F>>;

export type PartialEntity<K extends IEntityBase<string> = IEntityBase<string>> = Omit<
    Partial<K>,
    'key'
> & {
    key: K['key'];
};

//  Utility
export type EntityToSave<T extends IEntityBase<string>> = Omit<
    T,
    Exclude<keyof IEntityBase<string>, 'key'>
>;

// Entities that can be shared to authorized third parties
export type SharedEntity =
    | Person
    | Job
    | Invoice
    | Conversation
    | Story
    | Photo
    | Campaign
    | CampaignEnrollment
    | Employee
    | User
    | Review
    | Case
    | Payment;

export type SharedEntityKind = SharedEntity['key']['kind'];

// Entity-based subscriptions
export type EntityBasedSubscriptions = Exclude<SharedEntityKind, 'User'>;

// Non-entity subscriptions
type NonEntitySubscriptions = 'notifications';

export type WebhookSubscription = EntityBasedSubscriptions | NonEntitySubscriptions;

export type ImportedNiceJobDataKinds =
    | 'Person'
    | 'Invoice'
    | 'Payment'
    | 'Job'
    | 'Visit'
    | 'Employee'
    | 'CatalogProduct';

//  Common elements
export interface Address {
    address: string | null;
    unit: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;

    googlePlaceId?: string | null;
    timezone?: string | null;
    geo?: {
        latitude: number | null;
        longitude: number | null;
    } | null;
}

export enum CreationMethod {
    SDK = 'SDK',
    API = 'API',
    MESSAGE = 'MESSAGE',
    SHARE = 'SHARE',
}

export type DomainKey = 'API' | 'APP' | 'MICROSITE' | 'TRACKING';

export interface Employee extends IEntityBase<'Employee'> {
    first_name: string;
    last_name: string | null;
    position: string | null;

    email: string | null;
    phone: string | null;

    profile_photo_id: string | null;
    user_id: string | null;

    stats: IEmployeeStats | null;
    insights: null | EmployeeInsights;

    channels: Array<EmployeeChannel>;

    archived_at: Date | null;
}

export interface IEmployeeStats {
    review_count: number;
    rating_sum: number;
}

export interface EmployeeChannel {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    position: string | null;
    network: string;
    external_source_id: string | null;
    created_at: Date;
    updated_at: Date | null;
}

export interface SupplementedEmployee extends Employee {
    initials: string;
    name: string;
    friendly_phone: string | null;
}

export interface PopulatedEmployee extends SupplementedEmployee {
    profile_photo: Photo | null;
}

export interface EmployeeInsights {
    review_count: ReviewCount;
    review_average_rating: ReviewAverageRating;
}

interface ReviewCount {
    v1: number;
}

interface ReviewAverageRating {
    v1: number;
}

export type EntityMetadata = {
    [field: string]:
        | string
        | number
        | boolean
        | null
        | Array<string | number | boolean | null>
        | undefined;
};

//  Entities
export interface IEntityBase<
    K extends string,
    NS extends string = string,
    ID extends string = string
> {
    key: IEntityKey<K, NS, ID>;
    id: ID;
    created_at: Date;
    updated_at: Date;
    deleted: boolean;
    deleted_at: Date | null;
}

export interface AccountInvitation extends IEntityBase<'AccountInvitation', 'Account'> {
    account_id: string;
    email: string;
    role: IAccountRole;
    used: boolean;
    used_at: Date;
    first_name: string;
    last_name: string;
}

export interface ActiveInstance extends IEntityBase<'ActiveInstance', 'Globals'> {
    last_active: Date;
    active: boolean;
}

export interface Authentication extends IEntityBase<'Authentication'> {
    network: NetworkName;
    type: 'company' | 'personal';
    auth:
        | ApiKeyAuthInfo
        | OAuthAuthorizationCodeAuthInfo
        | OAuthClientCredentialsAuthInfo
        | MindbodyAuthInfo;
    userProfile: IPartialAuthenticationProfile & {
        pages?: Array<IPartialAuthenticationProfile>;
    };

    companyProfile: IPartialAuthenticationProfile | null;
    data: Record<string, any> | null;

    hidden: boolean;
    pending: boolean;
    expired: boolean;

    deactivating: boolean;
    successor_authentication_id: string | null;

    user_id: string | null;
}

export type IPartialAuthenticationProfile = Pick<AuthenticationProfile, 'external_id' | 'name'> &
    Partial<Omit<AuthenticationProfile, 'external_id' | 'name'>>;

export enum AuthInfoType {
    OAUTH_CLIENT_CREDENTIALS = 'OAUTH_CLIENT_CREDENTIALS',
    OAUTH_AUTHORIZATION_CODE = 'OAUTH_AUTHORIZATION_CODE',
    API_KEY = 'API_KEY',
    MINDBODY = 'MINDBODY',
}

export interface AuthenticationAuthInfo {
    auth_info_type: AuthInfoType;
}

export interface ApiKeyAuthInfo extends AuthenticationAuthInfo {
    auth_info_type: AuthInfoType.API_KEY;
    api_key: string;
    encryptedRefreshToken?: never;
}

export interface OAuthAuthInfo extends AuthenticationAuthInfo {
    accessToken: string;
    expiresIn: number;
    renewed: Date | null;
    scope?: Array<string>;
}

export interface OAuthAuthorizationCodeAuthInfo extends OAuthAuthInfo {
    auth_info_type: AuthInfoType.OAUTH_AUTHORIZATION_CODE;
    encryptedRefreshToken: string;
}

export interface OAuthClientCredentialsAuthInfo extends OAuthAuthInfo {
    client_id: string;
    client_secret: string;
    additional_infos: OAuthClientCredentialsAdditionalInfoServiceTitan;
    encryptedRefreshToken?: never;
}

export interface OAuthClientCredentialsAdditionalInfoServiceTitan {
    tenant_id: string;
}

export interface MindbodyAuthInfo {
    auth_info_type: AuthInfoType.MINDBODY;
    site_id: string;
    accessToken: string;
    location_id: string;
    encryptedRefreshToken?: never;
}

export interface AuthenticationProfile {
    external_id: string;
    username: string | null;
    description: string | null;
    name: string;
    email: string | null;
    photo: string | null;
    phone: string | null;
    website: string | null;
    address: IAddress | null;
    company: string | null;
    position: string | null;
    data: any;
    scrapy: any;
    review_url: string | null;
    write_review_url: string | null;
    opening_date?: Date | null;
    initial_review_count?: number | null;

    disabled: boolean;
    disabled_reason: string | null;
    disabled_reason_short: string | null;

    accessToken: string | null;
    responsibid_server: 'bids' | 'beta' | null;

    network_custom_field_mappings?: Record<
        'have_left_review' | 'review_rating' | 'last_review_invite_date' | 'invite_to_review',
        string
    > | null;
}

export interface Call extends IEntityBase<'Call'> {
    legacy_lead_id: string | null;
    phone: string;
    to: string;
    status: 'forwarding' | 'completed' | 'no-answer' | 'failed';
    duration: number;
    twilio_call_sid: string;
    twilio_child_call_sid: string;
    twilio_recording_sid: string;
    twilio_recording_url: string;
    employee_ids: Array<string>;
    person_id: string;
    lead: boolean;
}

export interface Campaign<K extends IEntityBase<string> = IEntityBase<string>>
    extends IEntityBase<'Campaign'> {
    name: string;
    description?: string | null;
    processionType: 'SEQUENTIAL' | 'SINGLE';

    default_campaign: 'GET_REVIEWS' | 'RECOMMENDATIONS' | 'BOOKINGS' | null;
    objective: CampaignObjective | null;
    subject_kind: CampaignSubjectKind;

    active: boolean;
    hidden: boolean;
    default_hidden: boolean;
    deferred_campaign_id: string | null;
    multipleEnrollments: boolean;
    minimumReEnrollLatency: string | null;
    paused: boolean;
    primetimeOnly: boolean;
    is_modified?: boolean;

    entryRules: Array<CampaignRuleCollection<K>>;
    exitRules: Array<CampaignRuleCollection<K>>;
}

export enum ReviewCampaignEnrollmentLimit {
    ONE_DAY = '1 days',
    SIX_MONTHS = '180 days',
}

export interface CampaignRuleCollection<EntityKind extends IEntityBase<string>> {
    equivalency: number;
    rules: Array<CampaignRule<EntityKind>>;
}

export enum CampaignObjective {
    REVIEWS = 'REVIEWS',
    RECOMMENDATIONS = 'RECOMMENDATIONS',
    EMPLOYEE_REVIEWS = 'EMPLOYEE_REVIEWS',
    ENGAGEMENT = 'ENGAGEMENT',
    BOOKINGS = 'BOOKINGS',
    WELCOME_NEW_CUSTOMERS = 'WELCOME_NEW_CUSTOMERS',
    REWARD_LOYAL_CUSTOMERS = 'REWARD_LOYAL_CUSTOMERS',
    REPEAT_BUSINESS = 'REPEAT_BUSINESS',
}

export const GIFT_CAMPAIGN_OBJECTIVES: CampaignObjective[] = [
    CampaignObjective.WELCOME_NEW_CUSTOMERS,
    CampaignObjective.REWARD_LOYAL_CUSTOMERS,
];

export type GiftCampaignObjective =
    | CampaignObjective.WELCOME_NEW_CUSTOMERS
    | CampaignObjective.REWARD_LOYAL_CUSTOMERS;

// This type exists solely to add support of the GiftIntent to the Rules Engine.
export interface GiftIntent extends IEntityBase<'GiftIntent'>, IGiftIntent {}

export interface CampaignRule<EntityKind extends IEntityBase<string>> {
    equivalency: number;
    kind: EntityKind['key']['kind'];
    subjectField: Extract<keyof Omit<EntityKind, 'key'>, string> | 'id';
    rule: CampaignRuleSingleEntity<EntityKind> | CampaignRuleAggregate<EntityKind>;
}

export enum CampaignRuleOperator {
    GREATER_THAN_OR_EQUAL_TO = 'GREATER_THAN_OR_EQUAL_TO',
    GREATER_THAN = 'GREATER_THAN',
    EQUAL_TO = 'EQUAL_TO',
    LESS_THAN = 'LESS_THAN',
    LESS_THAN_OR_EQUAL_TO = 'LESS_THAN_OR_EQUAL_TO',
    NOT_EQUAL_TO = 'NOT_EQUAL_TO',
    CONTAINS = 'CONTAINS',
    DOES_NOT_CONTAIN = 'DOES_NOT_CONTAIN',
    IS_WITHIN_THE_LAST = 'IS_WITHIN_THE_LAST',
    IS_BEYOND_THE_LAST = 'IS_BEYOND_THE_LAST',
    IS_AFTER = 'IS_AFTER',
    IS_BEFORE = 'IS_BEFORE',
}

export enum CampaignSubjectKind {
    Person = 'Person',
    Story = 'Story',
    Employee = 'Employee',
    User = 'User',
}

export type EntityPropertyScalarType = string | boolean | number | Date | null;
export type EntityPropertyType = EntityPropertyScalarType | Array<EntityPropertyScalarType>;
export type CampaignRuleValue = EntityPropertyScalarType | EntityPropertyScalarType[];
export enum CampaignRuleCalcMethod {
    SUM = 'SUM',
    MEAN = 'MEAN',
    MEDIAN = 'MEDIAN',
    MODE = 'MODE',
}

export interface EntityPropertyRule<
    EntityKind extends IEntityBase<string>,
    Calc extends CampaignRuleCalcMethod = CampaignRuleCalcMethod
> {
    calc?: Calc; // Default: 'SUM'
    property: Extract<keyof Omit<EntityKind, 'key'>, string> | '_COUNT_' | 'network';
    operator?: CampaignRuleOperator; // Default EQUAL_TO
    value: CampaignRuleValue;
    equivalency: number;
}

export enum CampaignRuleType {
    SINGLE = 'SINGLE',
    AGGREGATE = 'AGGREGATE',
}

export interface CampaignRuleEntityFilter<EntityKind extends IEntityBase<string>>
    extends EntityPropertyRule<EntityKind, CampaignRuleCalcMethod.SUM> {}

export interface CampaignRuleAggregateFilter<EntityKind extends IEntityBase<string>>
    extends EntityPropertyRule<EntityKind> {}

export interface CampaignRuleSingleEntity<EntityKind extends IEntityBase<string>> {
    type: CampaignRuleType.SINGLE;
    entity_filters: Array<CampaignRuleEntityFilter<EntityKind>>;

    // aggregate_filters are IGNORED for SINGLE-type rules
    aggregate_filters?: null | Array<CampaignRuleAggregateFilter<EntityKind>>;
}
export interface CampaignRuleAggregate<EntityKind extends IEntityBase<string>> {
    type: CampaignRuleType.AGGREGATE;
    entity_filters: Array<CampaignRuleEntityFilter<EntityKind>>;
    aggregate_filters: Array<CampaignRuleAggregateFilter<EntityKind>>;
}

export interface CampaignEnrollment extends IEntityBase<'CampaignEnrollment'> {
    campaign_id: string;
    legacy_sequence_queue_id: string | null;
    tags: Array<string>;

    subjectNamespace: string | null;
    subjectKind: Campaign['subject_kind'];
    subjectId: string;

    user_id: string | null;
    employee_ids: Array<string>;
    legacy_user_id: string;

    entryEntities: Array<IEntityKey>;
    story_id: string | null;

    successful: boolean;
    successful_at: Date | null;
    successEntities: Array<IEntityKey>;

    usurped: boolean;
    usurped_at: Date | null;
    usurped_by: string | null;

    eligible: boolean;

    ended: boolean;
    ended_at: Date | null;

    paused: boolean;
    stopped: boolean;
    stopped_at: Date | null;
    stopped_by: string | null;

    immediate_exit: boolean;
}

export interface CampaignEnrollmentProfile
    extends Pick<
            CampaignEnrollment,
            | 'id'
            | 'subjectNamespace'
            | 'subjectKind'
            | 'subjectId'
            | 'created_at'
            | 'successful'
            | 'ended'
            | 'stopped'
            | 'usurped'
        >,
        Pick<
            SupplementedPerson,
            | 'first_name'
            | 'last_name'
            | 'name'
            | 'initials'
            | 'email'
            | 'phone'
            | 'company'
            | 'position'
        > {}

export interface CampaignRequest extends IEntityBase<'CampaignRequest'> {
    slug: string | null;
    legacy_message_id: string | null;

    campaign_id: string;
    campaign_enrollment_id: string;
    campaign_stage_id: string;
    message_template_id: string | null;
    content_template_id: string | null;
    gift_configuration_id: string | null;
    gift_quantity: number | null;
    skipped: boolean;
    skipped_because?:
        | null
        | 'MESSAGE_SEND_FAILED'
        | 'MESSAGE_DELIVERY_FAILED'
        | 'GIFT_INTENT_CREATION_FAILED'
        | 'LANDLINE_PHONE'
        | 'SUBJECT_UNSUBSCRIBED'
        | 'SUBJECT_BLOCKED'
        | 'SUBJECT_MISSING_CONTACT_INFO'
        | 'SUBJECT_MISSING_CONSENT'
        | 'CAMPAIGN_INACTIVE'
        | 'CAMPAIGN_PAUSED'
        | 'CAMPAIGN_STAGE_PAUSED'
        | 'CAMPAIGN_ENROLLMENT_PAUSED';
    sent: boolean;
    method: MessageTemplate['method'] | null;
    email_id: string | null;
    sms_id: string | null;
    gift_intent_id: string | null;
    successful: boolean;
}

export interface CampaignStage extends IEntityBase<'CampaignStage'> {
    campaign_id: string;
    message_template_id: string | null;
    content_template_id: string | null;
    gift_configuration_id: string | null;
    gift_quantity: number | null;
    method: MessageTemplateMethod | null;
    rules: Array<CampaignRuleCollection<IEntityBase<string>>> | null;
    paused: boolean;
    order: number;
    wait: string;
}

export interface PopulatedSugarwishGiftConfigurationParameters
    extends ISugarwishGiftConfigurationParameters {
    content_template: IContentSchema<IContentMedium>;
}

export interface PopulatedGiftConfiguration extends Omit<IGiftConfiguration, 'parameters'> {
    gift_template: IGiftTemplate;
    parameters: PopulatedSugarwishGiftConfigurationParameters;
}

export interface PopulatedCampaignStage extends CampaignStage {
    messageTemplate: MessageTemplate | null;
    contentTemplate: ContentTemplate | null;
    // @todo @gifting: We might need to remove this imported type. It is using the Data
    // Platform's type, and Data-Platform relies on this type file.
    gift_configuration: PopulatedGiftConfiguration | null;
}

export interface CampaignStageData {
    id: string;
    sends: number;
    reads: number;
    clicks: number;
    successes: number;
}

export interface CampaignStats {
    company_id: string;
    campaign_id: string;
    total_enrollments: number;
    total_successful_entities: number;
    successful_enrollments: number;
    total_unique_exited_subjects: number;
    data_points: Array<CampaignDataPoint>;
    campaign_stage_summaries: Array<CampaignStageSummary>;
    meta_data: {
        scale: StatsScale;
        date_format: string;
        interval: number;
    };
}

export interface CampaignDataPoint {
    successes: number;
    successes_change: number;
    start_date: Date;
    end_date: Date;
    campaign_stages: Array<DataPointCampaignStage>;
}

export interface DataPointCampaignStage {
    id: string;
    method: MessageTemplateMethod;
    successes: number;
    successes_change: number;
    deleted: boolean;
    deleted_at: Date | null;
}

export interface CampaignStageSummary {
    id: string;
    method: MessageTemplateMethod;
    sends: number;
    opens: number;
    clicks: number;
    successes: number;
    unsubscribes: number;
    deleted: boolean;
    deleted_at: Date | null;
}

export enum ReviewJourneyModalStep {
    REVIEW_SOURCES = 'review_sources',
    APPS = 'apps',
    INVITE_PAST_CUSTOMERS = 'invite_past_customers',
    START_AUTOMATION = 'start_automation',
}

export interface CompanyInvitation extends IEntityBase<'CompanyInvitation'> {
    email: string;
    role: ICompanyRole;
    used: boolean;
    used_at: Date;
    first_name?: string | null;
    last_name?: string | null;
}

export interface Case extends IEntityBase<'Case'> {
    network: string;
    external_source_id: string;
    person_id: string;
    integration_id: string;
    employee_ids: Array<string>;

    external_created_at: Date;
    status: CaseStatus;
    name: string | null;
    completed_at: Date | null;
    description: string | null;
    metadata: Record<string, any>;
    tags: Array<string>;
}

export enum CaseStatus {
    pending = 'pending',
    open = 'open',
    closed = 'closed',
}

export interface CatalogProduct extends IEntityBase<'CatalogProduct'> {
    name: string;
    description: string | null;
    channels: Array<CatalogProductChannel>;
}

export interface CatalogProductChannel {
    id: string;
    network: string;
    external_source_id: string | null;
    status: CatalogProductStatus;
    name: string;
    description: string | null;
    created_at: Date;
    updated_at: Date;
}

export enum CatalogProductStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
}

export interface CompanyAliases extends IEntityBase<'CompanyAliases', 'Globals'> {
    slug: Array<string>;
    legacy_company_id: string | null;
    reviewly: boolean;
    monolith_encoded_company_id: string | null;
    short_id: string;
    search_fields: Array<string>;
}

export interface ConsentEvent extends IEntityBase<'ConsentEvent'> {
    person_id: string;
    channel: ConsentEventChannel;
    type: ConsentEventType;
    entity: {
        id: string;
        kind: (
            | CampaignEnrollment
            | Case
            | Conversation
            | Invoice
            | Job
            | LeadForm
            | Message
            | Payment
            | Person
            | Proposal
            | SMSUnsubscribe
            | Unsubscribe
            | Visit
        )['key']['kind'];
    };
    occurred_at: Date;
}

export enum ConsentChannel {
    EMAIL = 'EMAIL',
    SMS = 'SMS',
    DEFAULT = 'DEFAULT',
    ALL = 'ALL',
}

export type ConsentRegionRuleChannel = Exclude<ConsentChannel, ConsentChannel.ALL>;

export type ConsentEventChannel = Exclude<ConsentChannel, ConsentChannel.DEFAULT>;

/**
 * must be updated along with
 *   - ConsentEventType schema
 *   - ConsentRegionTypeMap schema
 *   - default ConsentRegions
 */
export enum ConsentEventType {
    CASE_CREATED = 'CASE_CREATED',
    CONVERSATION_CREATED = 'CONVERSATION_CREATED',
    INVOICE_CREATED = 'INVOICE_CREATED',
    JOB_CREATED = 'JOB_CREATED',
    LEAD_FORM_CREATED = 'LEAD_FORM_CREATED',
    MANUAL_CAMPAIGN_ENROLLMENT_CREATED = 'MANUAL_CAMPAIGN_ENROLLMENT_CREATED',
    MESSAGE_CREATED = 'MESSAGE_CREATED',
    PAYMENT_CREATED = 'PAYMENT_CREATED',
    PERSON_CREATED = 'PERSON_CREATED',
    PERSON_CREATED_VIA_IMPORT = 'PERSON_CREATED_VIA_IMPORT',
    PERSON_CREATED_VIA_INVITE = 'PERSON_CREATED_VIA_INVITE',
    PROPOSAL_CREATED = 'PROPOSAL_CREATED',
    UNSUBSCRIBED = 'UNSUBSCRIBED',
    VISIT_CREATED = 'VISIT_CREATED',
}

export enum ConsentType {
    REJECTED = 'REJECTED',
    IMPLIED = 'IMPLIED',
    EXPRESS = 'EXPRESS',
}

export interface Conversation extends IEntityBase<'Conversation'> {
    network: string;
    external_source_id: string;
    external_created_at: Date;

    person_ids: Array<string>;
    employee_ids: Array<string>;

    tags: Array<string>;
    status: ConversationStatus | null;
    rating: ConversationRating | null;
    completed_at: Date | null;
}

export interface ConversationRating {
    id: string | null;
    kind: ParticipantKind;
    rating: number;
    comment: string;
}

export enum ConversationStatus {
    open = 'open',
    closed = 'closed',
    snoozed = 'snoozed',
}

export interface Convert extends IEntityBase<'Convert'> {
    domain: string | null;
    asana_task_id: string | null;
    customers: {
        why_call: string | null;
        common_objections: string | null;
    } | null;
    services: {
        primary_service: string | null;
        secondary_service: string | null;
        company_goal: string | null;
    } | null;
    locations: {
        primary_city: string | null;
        other_city: string | null;
    } | null;
    what_sets_apart: {
        top_reason: string | null;
        company_story: string | null;
        guarantee: string | null;
    } | null;
    credentials: {
        awards: string | null;
        certifications: string | null;
        liability_insurance: string | null;
    } | null;
    website_preferences: {
        style_preferences: string | null;
        style_examples: string | null;
    } | null;
    assets: {
        badge: Array<string>;
        brand: Array<string>;
        image: Array<string>;
    } | null;
    seo: {
        investing: boolean | null;
        report_example: string | null;
    } | null;
    setup_status: {
        progress: number;
        current_page: string;
        migrated: boolean | null;
    };
    completed_at: Date | null;
}

export interface CallTracking extends IEntityBase<'CallTracking'> {
    tracking_phone?: string;
    recording_enabled?: boolean;
    tracking_phone_twilio_sid?: string;
    twilio_address_sid?: string;
    twilio_bundle_sid?: string;
}

export interface ContentTemplate<Medium extends IContentMedium = IContentMedium>
    extends IEntityBase<'ContentTemplate'>,
        IContentSchema<Medium> {
    base_content_template_id?: string | null;
    metadata?: Record<string, any>;
    tags: Array<string>;
}

/**
 * @type OmittedContentTemplate
 * @description This type is used to create a ContentTemplate without the key field
 */
export type OmittedContentTemplate = Omit<
    ContentTemplate,
    Exclude<keyof IEntityBase<string>, 'key'>
>;

/**
 * @type ContentTemplateLanguageDictionary
 * @description This type is used to create a dictionary of ContentTemplates, where the key is the language
 */
export type ContentTemplateLanguageDictionary = { [key in ILanguage]: OmittedContentTemplate };

export interface ConvertStats extends IEntityBase<'ConvertStats'> {
    date_joined: Date;
    total_stats: ConvertSubStats;
    monthly_stats: Array<ConvertSubStats>;
    daily_stats: Array<ConvertSubStats>;
}

interface ConvertSubStats {
    date: string;
    visitors_web_total: number;
    visitors_web_cpc: number;
    visitors_web_direct: number;
    visitors_web_organic: number;
    visitors_web_referral: number;
    leads_web_total: number;
    leads_web_cpc: number;
    leads_web_direct: number;
    leads_web_organic: number;
    leads_web_referral: number;
    leads_phone_total: number;
}

export interface ConvertStatsV2 extends ConvertStatsV2Fields {
    company_id: string;
}

export interface ConvertStatsV2AllCompanies extends ConvertStatsV2Fields {
    company_data: Array<ConvertStatsV2>;
}

interface ConvertStatsV2Fields {
    leads: {
        lead_count: number;
        web_lead_count: number;
        phone_lead_count: number;
        data_points: Array<{
            lead_count: number;
            lead_change: number;
            lead_percent_change: number;
            phone: {
                lead_count: number;
                lead_change: number;
                lead_percent_change: number;
            };
            web: {
                lead_count: number;
                lead_change: number;
                lead_percent_change: number;
            };
            start_date: number;
            end_date: number;
        }>;
    };
    visitors: {
        visitor_count: number;
        data_points: Array<{
            visitor_count: number;
            visitor_change: number;
            visitor_percent_change: number;
            start_date: number;
            end_date: number;
        }>;
    };
    channels_summaries: {
        [key: string]: {
            lead_count: number;
            visitor_count: number;
        };
    };

    meta_data: {
        scale: StatsScale;
        date_format: string;
    };
}

export interface Email extends IEntityBase<'Email'> {
    message_template_id?: string;
    inputVariables: {
        [variable_name: string]: string | Array<string> | undefined;
    };

    content_template_id?: string;
    content_template_render_arguments?: Record<string, any>;

    to: Omit<CommunicationAddressee, 'phone'>;
    sending_at: Date;
    service: string | null;

    subject: string | null;
    content: string;
    fingerprint: string;
    duplicate: boolean;
    reply_to: string | null;

    from: Omit<CommunicationAddressee, 'phone'>;
    sent_at: Date | null;
    success: boolean;
    failure_reason: string | null;

    risk_profile: MessageRiskProfile;

    //  Email-specific
    mandrill_status: string | null;
    mandrill_email_id: string | null;
    mandrill_reject_reason: string | null;
    mandrill_error_name: string | null;
    mandrill_error_message: string | null;
}

export interface MessageRiskProfile {
    uris: Array<{
        uri: string;
        threat_types: ThreatType[];
    }>;
}

interface CommunicationAddressee {
    email: string;
    name: string | null;
    phone: string;
}

export interface Goal extends IEntityBase<'Goal'> {
    topic: GoalTopic;
    current_count: number;
    target_count: number;
    achievement_entity_id: string | null;
    achievement_entity_kind: string | null;
    achievement_entity_namespace: string | null;
    user_id: string;
    is_achieved: boolean;
    achieved_at: Date | null;
}

export enum GoalTopic {
    CALL_CREATED = 'CALL_CREATED',
    LEAD_FORM_CREATED = 'LEAD_FORM_CREATED',
    REVIEW_CREATED = 'REVIEW_CREATED',
    RECOMMENDATION_CREATED = 'RECOMMENDATION_CREATED',
}

export interface GoogleAnalytics extends IEntityBase<'GoogleAnalytics'> {
    account_id: string;
    measurement_id: string | null;
    property_id: string | null;
    property_id_ga4: string | null;
    profile_id: string | null;
    tag: string | null;
    tag_ga4: string | null;
}

export interface Integration extends IEntityBase<'Integration'> {
    authentication_id: string;
    user_id: string | null;
    network: string;
    profile: AuthenticationProfile;
    deactivating: boolean;
    disabled: boolean;
    inactive: boolean;
    expired: boolean;
    auto_invite: boolean;
    sync_leads: boolean;
    invite_delay: number;
    settings: any | null;
}

export interface Invoice extends IEntityBase<'Invoice'> {
    network: string;
    external_source_id: string;
    person_ids: Array<string>;
    integration_id: string;
    employee_ids: Array<string>;
    tags: Array<string>;

    total: number;
    subtotal: number | null;
    balance: number;
    discount: number;
    discounts: Array<{
        external_id?: string | null;
        name?: string | null;
        description?: string | null;
        amount: number | null;
        percentage?: number | null;
    }>;
    taxes: Array<{
        name?: string;
        amount: number | null;
        percentage?: number | null;
    }>;
    currency: string | null;
    line: Array<{
        external_id: string | null;
        amount: number;
        description: string | null;
    }>;

    external_invoice_name: string | null;
    external_created_at: Date;
    status: InvoiceStatus | null;
    paid: boolean;
    paid_at: Date | null;
    description: string | null;
    metadata: Record<string, any>;
}

export enum InvoiceStatus {
    DRAFT = 'draft',
    OPEN = 'open',
    PAID = 'paid',
    UNCOLLECTIBLE = 'uncollectible',
    VOID = 'void',
}

export interface Item {
    catalog_product_id: string;
    external_source_id: string;
    name: string;
    description?: string | null;
    amount: Amount;
    quantity: number;
}

export interface Job extends IEntityBase<'Job'> {
    employee_ids: Array<string>;
    user_ids: Array<string>;
    photo_ids: Array<string>;
    name: string | null;
    tags: Array<string>;
    external_created_at: Date;
    scheduled_at: Date | null;
    completed_at: Date | null;
    complete: boolean | null;
    amount: number;
    items?: Array<Item>;

    invoice_id: string;
    integration_id: string;
    network: string;
    external_source_id: string;
    person_ids: Array<string>;
}

export interface KeyTranslation extends IEntityBase<'KeyTranslation', 'Globals'> {
    input_key: KeyTranslationInputKey;
    input_value: string;
    output_key: KeyTranslationOutputKey;
    output_value: string;
}

export type KeyTranslationInputKey =
    | 'clio_company_id'
    | 'clio_webhook_url'
    | 'companycam_company_id'
    | 'company_id'
    | 'company_sdk_public_key'
    | 'freshbooks_webhook_url'
    | 'google_location_id'
    | 'housecallpro_company_id'
    | 'hubspot_company_id'
    | 'intercom_company_id'
    | 'jobber_company_id'
    | 'jobber_v2_company_id'
    | 'mindbody_company_id'
    | 'paypal_company_id'
    | 'paystone_hub_organization_id'
    | 'paystone_hub_user_id'
    | 'quickbooks_company_id'
    | 'responsibid_company_id'
    | 'share_id'
    | 'square_company_id'
    | 'stripe_company_id'
    | 'xero_company_id';

export type KeyTranslationOutputKey =
    | 'company_id'
    | 'clio_webhook_secret'
    | 'clio_custom_field_id'
    | 'freshbooks_webhook_secret'
    | 'user_id';

export interface LandingPage extends IEntityBase<'LandingPage'> {
    legacy_page_id: string | null;
    asana_task_id: string | null;
    details: {
        location: string | null;
        service: string | null;
    };
    credentials: {
        awards: string | null;
        certifications: string | null;
        liability_insurance: string | null;
    };
    what_sets_apart: {
        top_reason: string | null;
        recognized_clients: string | null;
        ideal_neighborhoods: string | null;
    };
    additional: {
        content: string | null;
    };
    build_status: LandingPageBuildStatus | null;
    paused: boolean;
    paused_at: Date | null;
}

export enum LandingPageBuildStatus {
    READY = 'READY',
    BUILDING = 'BUILDING',
    LAUNCHING = 'LAUNCHING',
    LAUNCHED = 'LAUNCHED',
}

export interface LeadForm
    extends IEntityBase<'LeadForm'>,
        Omit<SDKBaseEventProperties, 'element_text_content' | 'element_href'> {
    data: Array<LeadFormData>;
    referrer: string | null;
    person_id: string;
    legacy_lead_id: string | null;
    fingerprint: string;
}

export interface LeadFormData {
    name: string;
    value: string | boolean;
}

export interface LeadWidget extends IEntityBase<'LeadWidget'> {
    custom_url: string;
    custom_fields: Array<{
        type: 'SHORTTEXT' | 'LONGTEXT' | 'CHECKBOX' | 'SELECTOR';
        label: string;
        required: boolean;
        options?: Array<string>;
    }>;
}

export interface WidgetActivity extends IEntityBase<'WidgetActivity'> {
    count: number;
    last_used_at: Date;
}

export interface LoginSession extends IEntityBase<'LoginSession'> {
    user_id: string;
    userAgent: {
        os: string;
        family: string;
        device: string;
        agent: string;
    };
}

export interface SelectedProduct {
    plan: CompanyProductPlan;
    product: CompanyProductId;
}

export enum OAuthOperationType {
    LOGIN = 'LOGIN',
    SIGN_UP = 'SIGN_UP',
}

export interface OnboardingOAuthState extends IEntityBase<'OnboardingOAuthState'> {
    phone?: string;
    oauth_operation_type?: OAuthOperationType;
    selected_products?: Array<SelectedProduct>;
    coupon?: string;
    referrer?: string;
    post_login_url?: string;
    language: LanguagePreference | null;
}

//  Note that this is the deprecated v1 Match entity
export interface Match extends IEntityBase<'Match'> {
    review_id: string;
    person_id: string;
    certain: boolean;
    perfect_match: boolean;
    similarity: number;
    initialed: boolean;
    probability: number;
    redirects: number;
    strong_redirects: number;
    confirmed: boolean;
    incorrect: boolean;
    version: string;
}
export interface PopulatedMatch extends Match {
    person: PopulatedPerson;
}

export interface Message extends IEntityBase<'Message'> {
    network: string;
    external_source_id: string;
    external_created_at: Date;

    conversation_id: string | null;

    from: Participant;
    to: Array<Participant>;

    // Status should be an enum but currently isn't used
    status: null;
    subject: string | null;
    content: string;
}

export interface Microsite extends IEntityBase<'Microsite'> {
    leads: {
        enable: boolean;
        text: string;
        title: string;
        submit_text: string;
    };
    checkins: {
        enroll_after_checkin: boolean;
        enroll_delay: number;
    };
}

export enum MigrationSource {
    PAYSTONE_HUB = 'paystone_hub',
}

export enum LanguagePreference {
    EN = 'en',
    FR = 'fr',
}

export enum LanguagePreferenceEntity {
    USER = 'user',
    COMPANY = 'company',
}

export interface Photo extends IEntityBase<'Photo'> {
    legacy_photo_id: string | null;
    mime: string | null;
    extension: string | null;
    user_id: string | null;
    type: PhotoType;
    network: string | null;
    review_source_id: string | null;
    integration_id: string | null;
    social_media_id: string | null;
    external_source_id: string | null;
    url: string;
    original_url: string;
    filename: string;
    dimensions: PhotoDimensions | null;
    size: number | null;
    variations: {
        facebook?: PhotoVariation;
    };
}

export enum PhotoType {
    AVATAR = 'AVATAR',
    APP_LOGO = 'APP_LOGO',
    FAV_ICON = 'FAV_ICON',
    IOS_ICON = 'IOS_ICON',
    LOGO = 'LOGO',
    POWERED_BY_LOGO = 'POWERED_BY_LOGO',
    PROFILE = 'PROFILE',
    STORY = 'STORY',
    CONTENT = 'CONTENT',
}

export interface PhotoVariation {
    url: string;
    dimensions: PhotoDimensions;
}

export interface PhotoDimensions {
    width: number;
    height: number;
}

export interface MessageTemplate extends IEntityBase<'MessageTemplate'> {
    name: string | null;
    language_preference_entity?: LanguagePreferenceEntity | null;
    legacy_template_id: string | null;
    legacy_parent_template_id: string | null;
    is_default_template: boolean;
    base_message_template_id: string | null;
    method: MessageTemplateMethod;
    permit_duplicate_send: boolean;
    input_variable_specifications: Array<{
        inputVariableName: string | null;
        outputVariableName: string;
        required?: boolean;
        metadata?: boolean;
        array?: boolean;
        scalar?: boolean;
        key?: {
            namespace?: MessageTemplateInputVariableEntity['key']['namespace'] | null;
            kind: MessageTemplateInputVariableEntity['key']['kind'];
        } | null;
        populate?: Array<MessageTemplateInputVariableSpecificationPopulate> | null;

        //  Additional datastore lookup filters
        filter?: Record<string, any>;
    }>;

    click_trackers?: Array<{
        domain?: DomainKey | null;
        href: string;
    }> | null;

    messageData: MessageTemplateMessageData;

    //  Email-only
    unsubscribe_tracker?: boolean;
    template_file?: string;
    template_html_type?: 'MJML' | 'EJS';
    has_plaintext_option?: boolean;
}

export type MessageTemplateInputVariableEntity =
    /** expected */
    | MessageTemplateInputVariableMigratedEntity
    | Goal
    | LeadForm
    | Match
    | Network
    | OAuth2Credential
    | OpportunityStats
    | Person
    | Photo
    | Recommendation
    | Review
    | Story
    | User
    | WebsitePerformanceStats

    /** unexpected */
    | Call
    | CampaignEnrollment
    | Employee;

export type MessageTemplateInputVariableDatastoreEntity = Exclude<
    MessageTemplateInputVariableEntity,
    MessageTemplateInputVariableMigratedEntity
>;

export type MessageTemplateInputVariableSpannerEntity = ICompany;

export type MessageTemplateInputVariableMigratedEntity = IEntityBase<'Company'>;

export interface OpportunityStats extends IEntityBase<'OpportunityStats'> {
    // TODO: flesh out before using
}

export interface WebsitePerformanceStats extends IEntityBase<'WebsitePerformanceStats'> {
    // TODO: flesh out before using
}

export interface MessageTemplateEmail extends MessageTemplate {
    method: MessageTemplateMethod.EMAIL;
    messageData: MessageTemplateMessageDataEmail;

    unsubscribe_tracker: boolean;
    template_file: string;
    template_html_type: 'MJML' | 'EJS';
    has_plaintext_option: boolean;
}

export interface MessageTemplateSMS extends MessageTemplate {
    method: MessageTemplateMethod.SMS;
    messageData: MessageTemplateMessageDataSMS;
}

export enum MessageTemplateMethod {
    EMAIL = 'EMAIL',
    SMS = 'SMS',
}

export interface MessageTemplateInputVariableSpecificationPopulate {
    from: string;
    to: string;
    kind: MessageTemplateInputVariableEntity['key']['kind'];

    namespace?: string | null;
    searchField?: string | null;
    fields?: Array<string>;
    populate?: Array<MessageTemplateInputVariableSpecificationPopulate>;
}

export interface MessageTemplateMessageData {
    from?: {
        email?: string | null;
        name: string | null;
        phone?: string | null;
    };
    to:
        | {
              email: string;
              name?: string | null;
          }
        | {
              phone: string;
              name?: string | null;
          };
    subject?: string;
    body: any;
}

export interface MessageTemplateMessageDataEmail extends MessageTemplateMessageData {
    from?: {
        email?: string | null;
        name: string | null;
    };
    to: {
        email: string;
        name?: string | null;
    };
    subject: string;
}

export interface MessageTemplateMessageDataSMS extends MessageTemplateMessageData {
    from?: {
        email?: string | null;
        name: string | null;
    };
    to: {
        phone: string;
        name?: string | null;
    };
}

export interface Amount {
    value: number;
    currency_code: string | null;
}

export interface Network extends IEntityBase<'Network', 'Globals', NetworkName> {
    name: string;
    colour: string | null;
    website: string | null;
    icon_url: string | null;
    method:
        | 'APIKey'
        | 'Mindbody'
        | 'NiceJobAPI'
        | 'OAuth'
        | 'OAuthClientCredentials'
        | 'ReviewSite'
        | 'Zapier';
    integration: boolean;
    reviewSource: boolean;
    socialMedia: boolean;
    hidden: boolean;
    api_key_url: string | null;
    review_site_template_url: string | null;
    signup_url: string | null;
    help_url: string | null;
    default_priority: number;
    auto_invite: boolean;
    sync_leads: boolean;
    description: string | null;
    auto_share_enabled: boolean;
    trans_key: string;
}

// The name of a Network is used to group networks in the front-end Integrations list page.
// See https://github.com/nicejobinc/microservices/tree/master/appengine-routes/frontend-route/src/pages/Settings/Product/Integrations/ChildComponents/common/utils.ts#L68
export enum NetworkName {
    ACCULYNX = 'acculynx',
    BBB = 'bbb',
    CAPTERRA = 'capterra',
    CITYSEARCH = 'citysearch',
    CLIO = 'clio',
    COMPANYCAM = 'companycam',
    CONVERT_LABS = 'convert_labs',
    DATACANDY = 'datacandy',
    DRCHRONO = 'drchrono',
    FACEBOOK = 'facebook',
    FIELDPULSE = 'fieldpulse',
    FRESHBOOKS = 'freshbooks',
    G2 = 'g2',
    GOOGLE = 'google',
    GOOGLE_RSV = 'google-rsv',
    HOMEADVISOR = 'homeadvisor',
    HOMESTARS = 'homestars',
    HOUSECALLPRO = 'housecallpro',
    HUBSPOT = 'hubspot',
    INSTAGRAM = 'instagram',
    INTERCOM = 'intercom',
    JOBBER = 'jobber',
    JOB_TREAD = 'job_tread',
    LIBRO = 'libro',
    LINKEDIN = 'linkedin',
    MARKATE = 'markate',
    MINDBODY = 'mindbody',
    NICEJOB = 'nicejob',
    PAYPAL = 'paypal',
    QDS = 'qds',
    QUICKBOOKS = 'quickbooks',
    RESPONSIBID = 'responsibid',
    SENDJIM = 'sendjim',
    SERVICEMONSTER = 'servicemonster',
    SERVICETITAN = 'servicetitan',
    SQUARE = 'square',
    STRIPE = 'stripe',
    TRIPADVISOR = 'tripadvisor',
    TWITTER = 'twitter',
    VONIGO = 'vonigo',
    XERO = 'xero',
    YELP = 'yelp',
    YP = 'yp',
    ZENDESK = 'zendesk',
    ZILLOW = 'zillow',
}

export interface OAuth extends IEntityBase<'OAuth', 'Globals'> {
    client_id: string;
    client_secret: string;
    auth_url: string | null;
    token_url: string | null;
    request_token_url: string | null;
    api_version: string | null;
    app_id: string | null;
}

export interface PasswordReset extends IEntityBase<'PasswordReset'> {
    email: string;
    user_id: string;
    code: string;

    sent: boolean;
    sent_at: Date | null;
    email_id: string | null;

    used: boolean;
    used_at: Date | null;
}

export interface Participant {
    id: string | null;
    kind: ParticipantKind;
}

export enum ParticipantKind {
    Person = 'Person',
    Employee = 'Employee',
    User = 'User',
}

export interface Payment extends IEntityBase<'Payment'> {
    amount: number;
    currency: string | null;
    external_created_at: Date;
    description: string | null;
    tags: Array<string>;
    metadata: Record<string, any>;
    status: PaymentStatus;
    disputed: boolean;
    refunded: boolean;
    amount_refunded: number;

    external_source_id: string;
    invoice_ids: Array<string>;
    integration_id: string;
    person_ids: Array<string>;
    employee_ids: Array<string>;
    network: string;
}

export enum PaymentStatus {
    succeeded = 'succeeded',
    pending = 'pending',
    failed = 'failed',
}

export interface Person extends IEntityBase<'Person'> {
    legacy_contact_id: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    address: Address | null;
    company: string | null;
    position: string | null;
    notes: string | null;
    tags: Array<string>;
    metadata: Record<string, any> | null;
    photo_id: string | null;
    lead: boolean;
    blocked: boolean;
    lifecycle_stage: LifecycleStageStatus | null;
    initial_source: string | null;
    initial_source_user_id: string | null;

    channels: Array<PersonChannel>;
    search_fields: Array<string>;
    latest_activity_date: Date;
    latest: Record<PersonActivityType, Date | null>;
    unsubscribed: boolean;
}

export enum PersonInitialSource {
    /**
     * Note: The initial source of the Person is a free string field. The values below represent
     * some of the values in use. Other values include any of the network Ids.
     */
    IMPORT = 'IMPORT',
    LEAD_FORM = 'LEAD_FORM',
    RECOMMENDATION = 'RECOMMENDATION',
    REFERRAL_WIDGET = 'REFERRAL_WIDGET',
    REFERRAL_LEAD_FORM = 'REFERRAL_LEAD_FORM',
    CHECK_IN = 'CHECK_IN',
    INVITE = 'INVITE',
}

export enum LifecycleStageStatus {
    lead = 'LEAD',
    customer = 'CUSTOMER',
}

export interface PersonActivity extends IEntityBase<'PersonActivity'> {
    person_id: string;
    activity_type: PersonActivityType;
    date: Date;
    user_id: string | null;
    entityKind: string;
    entityId: string;
}

export enum PersonActivityType {
    //  NB: Updating PersonActivityType? Check company-service/updateMaxUnitsCount's `ACTIVITIES_TO_EXCLUDE`
    //      variable to see if changes are necessitated there
    CallCreated = 'CallCreated',
    CallUpdated = 'CallUpdated',
    CampaignEnrollmentCreated = 'CampaignEnrollmentCreated',
    CampaignRequestClicked = 'CampaignRequestClicked',
    CampaignRequestRead = 'CampaignRequestRead',
    CampaignRequestSent = 'CampaignRequestSent',
    CaseCreated = 'CaseCreated',
    CaseUpdated = 'CaseUpdated',
    ClickCreated = 'ClickCreated',
    ConversationCreated = 'ConversationCreated',
    ConversationRated = 'ConversationRated',
    ConversationUpdated = 'ConversationUpdated',
    InvoiceCreated = 'InvoiceCreated',
    InvoicePaid = 'InvoicePaid',
    InvoiceUpdated = 'InvoiceUpdated',
    JobCompleted = 'JobCompleted',
    JobCreated = 'JobCreated',
    LeadCreated = 'LeadCreated',
    LifecycleStageLeadUpdated = 'LifecycleStageLeadUpdated',
    LifecycleStageCustomerUpdated = 'LifecycleStageCustomerUpdated',
    PaymentCreated = 'PaymentCreated',
    PaymentUpdated = 'PaymentUpdated',
    PersonBlocked = 'PersonBlocked',
    PersonCreated = 'PersonCreated',
    PersonUnblocked = 'PersonUnblocked',
    ProposalAccepted = 'ProposalAccepted',
    ProposalCreated = 'ProposalCreated',
    ProposalRejected = 'ProposalRejected',
    ProposalUpdated = 'ProposalUpdated',
    RecommendationCreatedRecommendee = 'RecommendationCreatedRecommendee',
    RecommendationCreatedRecommender = 'RecommendationCreatedRecommender',
    ReviewCreated = 'ReviewCreated',
    ReviewUpdated = 'ReviewUpdated',
    SessionCreated = 'SessionCreated',
    SessionUpdated = 'SessionUpdated',
    UnsubscribeCreated = 'UnsubscribeCreated',
    ViewCreated = 'ViewCreated',
    VisitCompleted = 'VisitCompleted',
    VisitCreated = 'VisitCreated',
}

export interface Product extends IEntityBase<'Product', 'Globals', CompanyProductId> {
    name: string;
    trans_key: string;
    subproduct_name: string;
    hidden: boolean;
    init_cost: number;
    tiered: boolean;
    unit_label: string;
    plans: Array<ProductPlan>;
}

export interface ProductPlan {
    plan: CompanyProductPlan;
    name: string;
    trans_key: string;
    description?: string | null;
    tiers?: Array<ProductPlanTier> | null;
    plan_date: CompanyProductPlanDate;
    monthly_cost: number | null;
    is_deprecated?: boolean;
    is_admin_only?: boolean;

    // TODO: Per-plan init_cost to overwrite Product.init_cost?
}

export interface ProductPlanTier {
    max_units: number;
    monthly_cost: number;
}

export interface Proposal extends IEntityBase<'Proposal'> {
    network: string;
    external_source_id: string;
    integration_id: string;
    person_ids: Array<string>;
    employee_ids: Array<string>;

    external_created_at: Date;
    completed_at: Date | null;
    status: ProposalStatus;
    name: string | null;
    description: string | null;
    tags: Array<string>;

    total: number;
    currency: string | null;
}

export enum ProposalStatus {
    OPEN = 'OPEN',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
}

export interface QueuedEvent<MetadataType extends IPubsubMetadata = IPubsubMetadata>
    extends IEntityBase<'QueuedEvent', 'Schedule'> {
    date: Date | null;

    queue: string;
    status: 'queued' | 'processing' | 'processed' | 'failed';
    published_at: Date | null;

    service: string;
    data: {
        action: string;
        input: Record<string, any> | null;
    };
    metadata: MetadataType;
}

export interface Recommendation extends IEntityBase<'Recommendation'> {
    creation_method: CreationMethod;
    network: string | null;
    origin_url: string | null;
    referrer_url: string | null;
    session_id: string | null;
    pageview_id: string | null;
    name: string | null;
    slug: string;
    status: RecommendationStatus;
    message_template_id: string | null;
    email_id: string | null;
    sms_id: string | null;
    from_person_id?: string;
    to_person_id?: string | null;
    user_id: string | null;
    employee_ids: Array<string>;
    has_converted: boolean;
    tags: Array<string>;
    metadata: EntityMetadata;
}

export enum RecommendationStatus {
    PENDING = 'PENDING',
    SENT = 'SENT',
    FAILED = 'FAILED',
    THIRD_PARTY_SHARE = 'THIRD_PARTY_SHARE',
}

export interface RecommendationsCount {
    count: number | null;
}

export interface RecommendationsRate {
    recommendation_rate: number;
    total_people: number;
    recommenders: number;
}

export interface ReferralLead {
    name: string;
    email: string;
    phone: string;
}

export interface ReferredPerson extends IEntityBase<'ReferredPerson'> {
    recommendation_id: string;
    person_id: string;
    is_customer: boolean;
}

export interface ResellerSettings extends IEntityBase<'ResellerSettings', 'Account'> {
    convert_visible: boolean;
    apps_visible: boolean;
    social_media_visible: boolean;
    review_channels_visible: boolean;
    microsite_visible: boolean;
    intercom_chat_visible: boolean;
    intercom_app_id: string | null;
    white_label_settings: ResellerWhiteLabelSettings;
}

interface ResellerWhiteLabelSettings {
    enabled: boolean;
    brand_name: string | null;
    address: string | null;
    app_domain: string | null;
    microsite_domain: string | null;
    tracking_domain: string | null;
    help_center_domain: string | null;
    sales_domain: string | null;
    whats_new_domain: string | null;
    app_icon_id: string | null;
    ios_icon_id: string | null;
    android_icon_id: string | null;
    fav_icon_id: string | null;
    powered_by_photo_id: string | null;
}

export interface Review extends IEntityBase<'Review'> {
    legacy_review_id: string | null;
    legacy_album_id: string | null;
    fingerprint: string;
    person_id: string;
    user_id: string | null;
    employee_ids: Array<string>;
    review_source_id: string;
    network: string;
    external_source_id: string | null;
    external_url: string | null;
    source_url: string | null;
    scraping_hub_job_id: string | null;
    author: Partial<Omit<PersonChannel, 'id' | 'network' | 'created_at' | 'updated_at'>> &
        Pick<PersonChannel, 'network'>;
    content: string | null;
    rating: number;
    potential_matches: Array<string>;
    match_id: string | null;
    match_status: 'UNPROCESSED' | 'PROCESSED' | 'CONFIRMED' | 'REJECTED' | 'V1';
    subratings: Array<{
        describer: string;
        rating: number;
        content: string;
    }>;
    replies: Array<{
        content: string;
        external_source_id: string | null;
        email: string | null;
        created_at: Date | null;
        updated_at: Date | null;
    }>;
    crawled_at: Date;
    deletion_story_ids: Array<string>;
    reinstated_at: Date | null;
    insights: null | {
        employee_ids: {
            v1: ReviewInsightsEmployeeIdsV1;
        };
        match: {
            v1: {
                matches: Array<{
                    person_id: string;
                    prob: number;
                }>;
                needs_confirmation: boolean;
            };
            v2: ReviewInsightsMatchV2;
            dorian: ReviewInsightsMatchDorianMajorSecond;
        };
        sentiment: {
            v1: {
                documentSentiment: {
                    magnitude: number;
                    score: number;
                };
                language: string;
                sentences: Array<{
                    sentiment: {
                        magnitude: number;
                        score: number;
                    };
                    text: {
                        beginOffset: number;
                        content: string;
                    };
                }>;
                ldrSentiment: number;
            };
        };
        topics: null | {
            v2: null | ReviewInsightsTopicsV2;
        };
        snippet: null | {
            v1: null | ReviewInsightsSnippetV1;
        };
    };
    stats: {
        employee_ids: Array<string>;
    };
}

export interface ReviewSiteVerification extends IEntityBase<'ReviewSiteVerification'> {
    network: string;
    user_id: string;
    url: string;
    profile: AuthenticationProfile;
    migrated: boolean;
    pending: boolean;
    deactivating: boolean;
    successor_authentication_id: string | null;
    successor_rsv_id: string | null;
}

export type ReviewInsightsEmployeeIdsV1 = Array<string>;

export interface ReviewInsightsSnippetV1 {
    prediction_id?: string | null;
    snippets: Array<{
        content: string;
        score: number;
    }>;
}
export interface ReviewInsightsMatchV2 {
    candidates: Array<{
        person_id: string;
        distance: number;
    }>;
    needs_confirmation: boolean;
}

export interface ReviewInsightsMatchDorianMajorSecond {
    interval: 'major_second'; // Union type; update with more interval options as they come available
    candidates: Array<{
        person_id: string;
    }>;
    perfect_match: boolean;
}

export type ReviewInsightsTopicsV2 = Record<TopicSlug, number>;

export interface ReviewScrape extends IEntityBase<'ReviewScrape'> {
    review_source_id: string;
    spider: string;
    job_id: string;
    fetched: boolean;
    timestamp: Date;

    update_reviews: boolean;
    ignore_pre_crawl: boolean;
    initial_crawl: boolean;

    crawl_url: string;
    crawl_status: string;
}

export interface ReviewSource extends IEntityBase<'ReviewSource'> {
    authentication_id: string | null;
    review_site_verification_id: string | null;
    user_id: string;
    network: string;
    profile: AuthenticationProfile;
    inactive: boolean;
    deactivating: boolean;
    disabled: boolean;
    expired: boolean;
    settings: any;
    priority: number;
    migrated_at: Date | null;
}

export interface ReviewStats extends IEntityBase<'ReviewStats'> {
    review_count: number;
    rating_sum: number;
    invite_count: number;
    date_joined: Date;
    daily_stats: Array<ReviewSubStats>;
    monthly_stats: Array<ReviewSubStats>;
    since_joined_stats: ReviewSubStats;
}

export interface ReviewSubStats {
    date: string;
    review_count: number;
    rating_sum: number;
    invite_count: number;
    sub_stats: Array<{
        network: string;
        review_count: number;
        rating_sum: number;
    }>;
}

interface ReviewStatsV2Fields {
    review_count: number;
    rating_sum: number;
    review_count_by_rating: Array<{
        rating: number;
        count: number;
    }>;
    review_source_summaries: Array<{
        review_count: number;
        rating_sum: number;
        network: string;
    }>;
    data_points: Array<{
        review_count: number;
        rating_sum: number;
        review_change: number;
        rating_change: number;
        average_rating: number;
        start_date: number;
        end_date: number;
        review_source_data: Array<{
            review_count: number;
            rating_sum: number;
            rating_change: number;
            review_change: number;
            average_rating: number;
            network: string;
        }>;
    }>;
    meta_data: {
        scale: StatsScale;
        date_format: string;
        interval: number;
    };
}

export interface ReviewStatsV2 extends ReviewStatsV2Fields {
    company_id: string;
}

export interface ReviewStatsV2AllCompanies extends ReviewStatsV2Fields {
    company_data: ReviewStatsV2[];
}

export enum StatsScale {
    MONTH = 'month',
    WEEK = 'week',
    DAY = 'day',
}

export interface ScheduledEvent<MetadataType extends IPubsubMetadata = IPubsubMetadata>
    extends IEntityBase<'ScheduledEvent', 'Schedule'> {
    date: Date;

    waiting: boolean;
    failed: boolean;
    published: Date | null;

    data: Record<string, any> | 'keepWarm';
    metadata: MetadataType;
    topic: string | null;
    httpCloudFunction: string | null;
    keepWarm: boolean;

    cron: boolean;
    interval: number | null;
}

export interface Share extends IEntityBase<'Share'> {
    status: 'ready' | 'shared' | 'failed';

    //  Share content
    content_template_id: string | null;
    destination_url: string | null;
    caption: string | null;
    title: string | null;
    description: string | null;
    image_url: string | null;
    image_alt: string | null;

    //  Share content (legacy)
    story_id: string | null;

    //  Share on network
    social_media_id: string;
    network: string | null;
    external_source_id: string | null;
    external_url: string | null;

    //  Share context
    integration_id: string | null;
    user_id: string | null;
    auto_shared: boolean;
}

export interface SMS extends IEntityBase<'SMS'> {
    message_template_id?: string;
    inputVariables: Email['inputVariables'];

    content_template_id?: string;
    content_template_render_arguments?: Email['content_template_render_arguments'];

    to: Omit<CommunicationAddressee, 'email' | 'name'>;
    from: null | Omit<CommunicationAddressee, 'email' | 'name'>;
    sending_at: Date;
    service: string;

    content: string;
    attachments: Array<string>;
    fingerprint: string;
    duplicate: boolean;

    sent_at: Date | null;
    success: boolean;
    failure_reason: string | null;

    risk_profile: MessageRiskProfile;

    //  SMS-specific
    twilio_status: string | null;
    twilio_sms_sid: string | null;
    twilio_error_code: string | null;
    twilio_error_message: string | null;
}

export interface SocialMedia extends IEntityBase<'SocialMedia'> {
    authentication_id: string;
    user_id: string | null;
    network: string;
    profile: AuthenticationProfile;
    deactivating: boolean;
    disabled: boolean;
    inactive: boolean;
    expired: boolean;
    auto_share: boolean;
    auto_share_minimum_rating: number;
    settings: any | null;
}

export interface StripeCustomer extends IEntityBase<'StripeCustomer', 'Account'> {
    stripeCustomerId: string;
    credit_card_provider: string | null;
    credit_card_ending: number | null;
    coupon: {
        name: string;
        code: string;
        value: number | null;
        percent: number | null;
        type: 'ONE_TIME' | 'MONTHLY';
    } | null;
    country: string;
    state: string;
    postal_code: string;
    tax_rate: number;
    default_tax_rates: Array<string>;
    testing: boolean;
    reviewly: boolean;
}

export interface Story extends IEntityBase<'Story'> {
    review_ids: Array<string>;
    has_reviews: boolean;
    photo_ids: Array<string>;
    has_photos: boolean;
    share_ids: Array<string>;
    has_shares: boolean;

    campaign_enrollment_ids: Array<string>;
    employee_ids: Array<string>; // Manually-attributed
    person_ids: Array<string>; // Manually-attributed

    legacy_album_id: string | null;
    legacy_shared: boolean;
    legacy_reach: number | null;

    published: boolean;
    caption: string | null;
    location: string | null;
    tags: Array<string>;
    user_id: string | null;
    slug: string;
    hidden: boolean;
    sort_date: Date;
    deleted_preservable: boolean;
    reinstated_at: Date | null;
    has_potential_matches: boolean;
    review_sentiments: Array<'pos' | 'neu' | 'neg'>;
    review_networks: Array<string>;

    stats: null | {
        employee_ids: null | Array<string>;
        person_ids: null | Array<string>;
        topics: null | Array<TopicSlug>;
        has_reply: boolean;
    };
    network: string | null;
    external_source_id: string | null;
}

export interface Tag extends IEntityBase<'Tag'> {
    name: string;
    search_fields: Array<string>;
}

export interface Topic extends IEntityBase<'Topic'> {
    id: string;
    slug: TopicSlug;
    display_name: string;
    phrase: string;
    mention_cutoff: number;
    bias: TopicBias;
    active: boolean;
}

export enum TopicSlug {
    people_on_time = 'people_on_time',
    people_late = 'people_late',
    people_quick_response = 'people_quick_response',
    people_slow_response = 'people_slow_response',
    people_friendly = 'people_friendly',
    people_not_friendly = 'people_not_friendly',
    people_reliable = 'people_reliable',
    people_not_reliable = 'people_not_reliable',
    people_professional = 'people_professional',
    people_not_professional = 'people_not_professional',
    people_organized = 'people_organized',
    people_disorganized = 'people_disorganized',
    people_knowledgeable = 'people_knowledgeable',
    people_not_knowledgeable = 'people_not_knowledgeable',
    people_communicative = 'people_communicative',
    people_uncommunicative = 'people_uncommunicative',
    people_lots_of_experience = 'people_lots_of_experience',
    people_limited_experience = 'people_limited_experience',
    people_careful = 'people_careful',
    people_careless = 'people_careless',
    people_great = 'people_great',
    people_poor = 'people_poor',
    service_great_results = 'service_great_results',
    service_poor_results = 'service_poor_results',
    service_affordable = 'service_affordable',
    service_expensive = 'service_expensive',
    service_reliable = 'service_reliable',
    service_not_reliable = 'service_not_reliable',
    service_professional = 'service_professional',
    service_not_professional = 'service_not_professional',
    service_fast = 'service_fast',
    service_slow = 'service_slow',
    company_low_effort = 'company_low_effort',
    company_high_effort = 'company_high_effort',
    company_recommend = 'company_recommend',
    company_not_recommend = 'company_not_recommend',
    company_guarantee_honoured = 'company_guarantee_honoured',
    company_guarantee_not_honoured = 'company_guarantee_not_honoured',
    company_easy_to_contact = 'company_easy_to_contact',
    company_hard_to_contact = 'company_hard_to_contact',
    company_lots_of_experience = 'company_lots_of_experience',
    company_limited_experience = 'company_limited_experience',
}

export enum TopicBias {
    POSITIVE = 'POSITIVE',
    NEGATIVE = 'NEGATIVE',
}

export interface UnifiedLog<ResultType, InputType = any> extends IEntityBase<'UnifiedLog'> {
    id: string;
    pubsubMessageId: string;
    topic: string;
    data: {
        input: InputType;
        result: ResultType;
    };
    metadata: IPubsubMetadata;

    error: boolean;
    errorMessage: string | null;
    partial: boolean;
    partial_data: string | null;

    created_at: Date;
    updated_at: Date;
}

export type AuthenticationSources = {
    google_account_id?: string | null;
    xero_account_id?: string | null;
};

export interface User extends IEntityBase<'User', 'User'> {
    legacy_user_id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    phone: string | null;
    address: Address;
    status: 'ACTIVE' | 'INACTIVE';
    is_admin: boolean;
    is_superadmin: boolean;
    banned: boolean;
    companies: Array<UserCompanyRoleAssociation>;
    accounts: Array<UserAccountRoleAssociation>;
    last_company_id: string | null;
    notifications: {
        sms_leads: boolean;
        email_leads: boolean;
        email_reviews: boolean;
        weekly_opportunity: boolean;
        monthly_opportunity: boolean;
        monthly_performance: boolean;
        daily_gift: boolean;
    };
    profile_photo_id: string;
    search_fields: Array<string>;
    admin_company_id: string | null;
    admin_user_id: string | null;
    owner_id: string;
    monolith_customer_rep: string;

    password: IUserPassword;
    previous_passwords: Array<User['password']>;
    failed_login_attempts: Array<Date>;
    risk_profile: ICalculateResponse | null;

    authentication_sources: AuthenticationSources | null;

    hidden: boolean;
    hidden_companies: Array<UserCompanyRoleAssociation>;
    hidden_accounts: Array<UserAccountRoleAssociation>;
    migrated_at: Date;
    migrated_from: MigrationSource | null;

    intercom_owner_id: string | null;
    intercom_owner_email: string | null;

    language_preference?: LanguagePreference | null;
}

export interface IUserPassword {
    hash: string;
    salt: string;
    method: 'BCRYPT' | 'SHA256';
    date: Date;
}

export interface Visit extends IEntityBase<'Visit'> {
    employee_ids: Array<string>;
    user_ids: Array<string>;
    photos_ids: Array<string>;
    name: string | null;
    tags: Array<string>;
    scheduled_at: Date | null;
    completed_at: Date | null;
    complete: boolean | null;
    amount: number | null;
    invoice_id: string;
    integration_id: string;
    network: string;
    external_source_id: string;
    job_id: string;
    person_id: string;
    external_created_at: Date;
}

export interface WebsiteActivity extends IEntityBase<'WebsiteActivity'> {
    lead_form_id: string | null;
    call_id: string | null;
}

export interface UserAccountRoleAssociation {
    account_id: string;
    role: IAccountRole;
}

export interface UserCompanyRoleAssociation {
    company_id: string;
    role: ICompanyRole;
}

/**
 * Namespace: OAuth2
 */
export interface OAuth2AccessToken extends IEntityBase<'OAuth2AccessToken', 'OAuth2'> {
    client_id: string;
    company_id: string | null;
    refreshToken: string | null;
    accessToken: string;
    expires_in: number;
    scopes: Array<Scope>;
    grant_type: GrantType;
    user_id: string | null;
    legacy_user_id: string | null;
    network: string | null;
    sandbox: boolean;
    api_version: OAuth2Credential['api_version'];
    iOS: boolean;
    expiry: Date;
    refreshed_at: Date;
}

type GrantType = 'client_credentials' | 'authorization_code' | 'refresh_token';

export interface OAuth2RefreshToken extends IEntityBase<'OAuth2RefreshToken', 'OAuth2'> {
    client_id: string;
    company_id: string | null;
    refreshToken: string;
    scopes: Array<Scope>;
    code: string | null;
    user_id: string | null;
    legacy_user_id: string | null;
    network: string | null;
    sandbox: boolean;
    api_version: OAuth2Credential['api_version'];
}

export interface OAuth2Credential extends IEntityBase<'OAuth2Credential', 'OAuth2'> {
    client_id: string;
    client_secret: string;
    sdk_public_key: string;
    approved_scopes: Array<Scope>;
    approved_redirect_uris: Array<string>;
    sandbox: boolean;
    network: string;
    company_name: string;
    contact: Record<'name' | 'email' | 'phone', string>;
    api_version: 'V0_1' | 'V2';
    privacy_policy: string;
    terms_of_use: string;
}

export interface OAuth2User extends IEntityBase<'OAuth2User', 'OAuth2'> {
    email: string;
    is_admin: boolean;
    is_superadmin: boolean;
    client_ids: Array<string>;
    last_client_id: string | null;

    password: User['password'];
    previous_passwords: User['previous_passwords'];
    failed_login_attempts: User['failed_login_attempts'];
}

export interface OAuth2UserInvitation extends IEntityBase<'OAuth2UserInvitation', 'OAuth2'> {
    client_id: string;
    email: string;
    used: boolean;
    used_at: Date | null;
}

export interface IntercomAppTokenLink extends IEntityBase<'IntercomAppTokenLink', 'OAuth2'> {
    intercomAccessToken: string;
    nicejobAccessToken: string;
}

// Webhook
export interface Webhook extends IEntityBase<'Webhook', 'OAuth2'> {
    client_id: string;
    endpoint_url: string;
    subscriptions: Array<WebhookSubscription>;
    secret: string;
    is_enabled: boolean;
    created_at: Date;
    updated_at: Date;
}

export interface WebhookPublication extends IEntityBase<'WebhookPublication', 'OAuth2'> {
    webhook_id: string;
    payload: WebhookPayload;
    endpoint_url: string;
    is_test: boolean;
    attempts: Array<WebhookPublicationAttempt>;
    created_at: Date;
    updated_at: Date;
}

export interface WebhookPublicationAttempt {
    response_code: HttpStatusCode;
    created_at: Date;
    error?: string;
}

export type WebhookPayload = WebhookEntityPayload | IPublishedNotification;

export type WebhookEntityPayload = {
    event_code: string;
    entity_id: string;
    company_id: string;
};

export type WebhookSignature = {
    timestamp: number;
    signed_payload: string;
};

export type WebhookMessage = {
    payload: WebhookPayload;
    signature: WebhookSignature;
};

/**
 * Namespace: "Tracking"
 */
export interface SDKSessionData {
    network: string;
    browser_id: string | null;
    company_id: string | null;
    ip: string;
    origin_url: string;
    referrer_url: string;
    accept: string;
    user_agent: string;
    browser: string;
    browser_fingerprint: string;
    browser_language: string;
    browser_timezone: number;
    browser_version: string;
    os: string;
    os_version: string;
    city: string;
    region: string;
    country: string;
    latitude: number;
    longitude: number;
    utm_source: string;
    utm_medium: string;
    utm_term: string;
    utm_campaign: string;
    utm_content: string;
    nicejob_click_id: string | null;
    google_click_id: string | null;
    facebook_click_id: string | null;
    screen_height: number;
    screen_width: number;
    last_active_at: Date;
    metadata: EntityMetadata;
}

export interface Session extends IEntityBase<'Session', 'Tracking'>, SDKSessionData {
    name: string | null;
    creation_method: CreationMethod;
    person_id: string | null;
    user_id: string | null;
    employee_ids: Array<string>;
    tags: Array<string>;
    last_active_at: Date;
}

export interface PageView extends IEntityBase<'PageView', 'Tracking'> {
    browser_id: SDKSessionData['browser_id'];
    company_id: string;
    session_id: string;
    origin_url: string;
    referrer_url: string;
    metadata: EntityMetadata;
}

export interface SDKBaseElementProperties<M extends EntityMetadata = EntityMetadata, D = any> {
    element_id: string;
    element_classes: Array<string>;
    element_data_attributes: {
        [attribute: string]: string | number | boolean | null;
    };
    element_text_content: string;
    element_href: string;
    data: D;
    tags: Array<string>;

    //  Metadata
    //  -> metadata.person_id confers person_id onto trackers
    metadata: Partial<M>;
}

export interface SDKBaseEventProperties extends SDKBaseElementProperties {
    browser_id: SDKSessionData['browser_id'];
    company_id: string | null;
    session_id: string;
    pageview_id: string;
    origin_url: string;
    referrer_url: string;
    network: string;
}

export interface Click extends IEntityBase<'Click', 'Tracking'>, SDKBaseEventProperties {}

export interface View
    extends IEntityBase<'View', 'Tracking'>,
        Omit<SDKBaseEventProperties, 'element_text_content' | 'element_href'> {}

export interface Unsubscribe extends IEntityBase<'Unsubscribe', 'Tracking'> {
    company_id: string;
    email: string;
    metadata: SDKBaseElementProperties['metadata'];
}

export interface SMSUnsubscribe extends IEntityBase<'SMSUnsubscribe', 'Tracking'> {
    company_id: string;
    phone: string;
    metadata: SDKBaseElementProperties['metadata'];
}

export interface ClickTracker extends IEntityBase<'ClickTracker', 'Tracking'> {
    slug: string;
    click: Pick<Click, 'company_id' | 'metadata'>;
    // Can be extended to: Partial<Omit<Click, keyof IEntityBase<string> | 'company_id'>> & {
    //     company_id: Click['company_id'];
    // };
    // --> Edit schema if editing!
    domain: DomainKey | null;
    href: string;
}

export interface ViewTracker extends IEntityBase<'ViewTracker', 'Tracking'> {
    slug: string;
    view: Pick<View, 'company_id' | 'metadata'>;
    // Can be extended to: Partial<Omit<View, keyof IEntityBase<string> | 'company_id'>> & {
    //     company_id: View['company_id'];
    // };
    // --> Edit schema if editing!
}

export interface UnsubscribeTracker extends IEntityBase<'UnsubscribeTracker', 'Tracking'> {
    slug: string;
    unsubscribe: Pick<Unsubscribe, 'company_id' | 'metadata' | 'email'>;
    // Can be extended to: Omit<Unsubscribe, keyof IEntityBase<string>>;
    // --> Edit schema if editing!
}

export interface SubscriptionUpdateRequest
    extends IEntityBase<'SubscriptionUpdateRequest', 'Account'> {
    order_id: string;
    success: boolean;
    error: string | null;

    testing: boolean;
    reviewly: boolean;

    user_id: string;
    company_id: string;
    account_id: string | null;
    stripeCustomerId: string | null;

    ignore_immediate_charges: boolean;
    dry_run: boolean;
    is_admin: boolean;

    stripe_charge_id: string | null;
    stripe_subscriptions_to_update: ISubscriptionUpdateIndex;
    stripe_subscription_items_to_create: ISubscriptionItemToCreateIndex;
    stripe_subscriptions_to_create: Array<ISubscriptionToCreate>;
    immediate_charges: Array<IImmediateCharge>;

    processed_at: Date;

    input: any;
    metadata: any;
}

export interface ISubscriptionItemToCreateIndex {
    [subscription_id: string]: Array<
        ISubscriptionItemToCreate & {
            subscription: string;
        }
    >;
}

export interface ISubscriptionItemToCreate {
    plan: string;
    quantity: number;
    metadata: Record<string, string>;
    prorate?: boolean;
}

export interface ISubscriptionToCreate {
    customer: string;
    default_tax_rates: Array<string>;
    items: Array<ISubscriptionItemToCreate>;
    trial_end?: number | 'now';
}

export interface ISubscriptionUpdateIndex {
    [subscription_id: string]: {
        items: Array<
            | {
                  id: string;
                  deleted: true;
              }
            | {
                  id: string;
                  quantity: number;
                  metadata: Record<string, string>;
              }
        >;
        trial_end?: number | 'now';
    };
}

export interface IImmediateCharge {
    company_id: string;
    unit_cost: number;
    quantity: number;
    product_name: string;
    product_plan: string;
    plan_date: CompanyProductPlanDate;
    charge_id: string;
    metadata: {
        default_tax_rates: string;
        tax_rate: string;
    };
}

/**
 * Namespace: Requests
 */

export interface APIRequest extends IEntityBase<'APIRequest', 'Requests'> {
    request: {
        method: string;
        url: string;
        query: Record<string, string>;
        body: Record<string, any>;
        headers: Record<string, string>;
        location: {
            city: string;
            region: string;
            country: string;
        };
        ip: string;
    };
    dt: number;
    status: number;
    response: {
        data: Record<string, any> | null;
        cursor: string | null;
        error: string | null;
    };
    server_error: string;
    api_version: 'V0_1' | 'V2';
    company_id: string;
    user_id: string;
    network: string;
}

export interface WebhookRequest<Body = Record<string, any>>
    extends IEntityBase<'WebhookRequest', 'Requests'> {
    status: WebhookStatus | null;
    external_webhook_id: string | null;
    source: string | null;
    company_id: string | null;
    integration_id: string | null;

    request: {
        method: string | null;
        url: string;
        query: JSON;
        body: Body;
        headers: JSON;
        location: {
            city: string | null;
            region: string | null;
            country: string | null;
        };
        ip: string | null;
    };

    responses: Array<{
        status: number | null;
        response: JSON;
        error: string | null;
        timestamp: Date | null;
        monolith: boolean | null;
    }>;
}

export enum WebhookStatus {
    QUEUED = 'queued',
    PROCESSED = 'processed',
    FAILED = 'failed',
    AUTH_EXPIRED = 'auth_expired',
    DEDUPLICATED = 'deduplicated',
    REQUEUED = 'requeued',
    SPLIT = 'split',
    SKIPPED = 'skipped',
}

/**
 * Populated entities
 */

//  Company
export interface SupplementedCompany extends ICompany {
    friendly_phone: string | null;
    address_string: string | null;
}

export interface PopulatedCampaignEnrollment extends CampaignEnrollment {
    ongoing: boolean;
    end_date: Date;
    campaignRequests: Array<CampaignRequest>;
    user: PopulatedUser;
    stopped_by_user: PopulatedUser | null;
}

export interface PopulatedStoryCampaignEnrollment extends CampaignEnrollment {
    ongoing: boolean;
    end_date: Date;
    subject: CampaignEnrollmentProfile;
}

export interface PopulatedCampaignRequest extends CampaignRequest {
    subjectKind: CampaignEnrollment['subjectKind'];
    subjectId: CampaignEnrollment['subjectId'];
}

export interface PopulatedCompany extends SupplementedCompany {
    logo_photo: Photo | null;
}

export interface UserCompany extends PopulatedCompany {
    role: IUserRole;
    account_role: boolean;
    reviewly: boolean;
    ld_company_user: {
        data: LDUser;
        hash: string;
    };
}

//  Person
export interface SupplementedPerson extends Person {
    name: string;
    initials: string;
    friendly_phone: string | null;
}

export interface PopulatedPerson extends SupplementedPerson {
    profile_photo: Photo | null;
}

export interface PopulatedPersonForPeople extends PopulatedPerson {
    campaignEnrollments: Array<CampaignEnrollment>;
    latest_review: Review | null;
}

//  ResellerSettings
export interface PopulatedResellerSettings extends ResellerSettings {
    white_label_settings: PopulatedResellerWhiteLabelSettings;
}

export interface PopulatedResellerWhiteLabelSettingsAdditions {
    android_icon?: Photo | null;
    app_icon?: Photo | null;
    ios_icon?: Photo | null;
    fav_icon?: Photo | null;
    powered_by?: Photo | null;
}

interface PopulatedResellerWhiteLabelSettings
    extends ResellerWhiteLabelSettings,
        PopulatedResellerWhiteLabelSettingsAdditions {}

//  Review
export interface SupplementedReview extends Review {
    author: SupplementedPersonChannel;
}

export interface PopulatedReview extends Review {
    person: SupplementedPerson;
}

export interface PopulatedStoryReview extends PopulatedReview {
    potentialMatches: Array<PopulatedMatch>;
}

//  Share
export interface PopulatedShare extends Share {
    user: User;
}

//  Story
export interface PopulatedStory extends Story {
    campaignEnrollments: Array<PopulatedStoryCampaignEnrollment>;
    people: Array<Person>;
    photos: Array<Photo>;
    reviews: Array<PopulatedStoryReview>;
    shares: Array<PopulatedShare>;
    user: User | null;
}

export interface SupplementedStory extends PopulatedStory {
    share_url: string;
}

//  User
export interface PopulatedTeamUser extends User {
    accountUser: boolean;
    role: IUserRole;
}

export interface SupplementedUser extends User {
    name: string;
    initials: string;
    friendly_phone: string | null;
}

export interface PopulatedUser extends SupplementedUser {
    profile_photo: Photo | null;
}

export interface PopulatedUserWithCompanies extends PopulatedUser {
    intercom_user_id_hash: string;
    intercom_user_id: string;
    intercom_user_email: string;
    intercom_user_name: string;
    Companies: Array<UserCompany>;
}

/**
 * Common subtypes
 */
export interface PersonChannel {
    id?: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: Address | null;
    company?: string | null;
    position?: string | null;
    metadata?: Record<string, any> | null;
    network: string;
    external_source_id?: string | null;
    created_at: Date;
    updated_at?: Date;
}
export interface SupplementedPersonChannel extends PersonChannel {
    name: string;
    username: string;
    initials: string;
    friendly_phone: string | null;
}

//  WebsiteActivity
export interface PopulatedWebsiteActivity extends WebsiteActivity {
    call: Call | null;
    leadForm: LeadForm | null;
}

export interface Segment extends IEntityBase<'Segment'> {
    description: string;
    subject_kind: CampaignSubjectKind;
    rule_collection: Array<{
        equivalency: number;
        rules: Array<CampaignRule<any>>;
    }>;
}

export interface PaymentSettings extends IEntityBase<'PaymentSettings'> {
    company_id: string;
    has_review_access: boolean;
    has_trial_reset?: boolean;
}

export interface ReviewCrawl extends IEntityBase<'ReviewCrawl'> {
    crawl_date: Date;
    error_message: string | null;
    review_source_id: string;
    status: 'HANGUP' | 'PROCESSING' | 'PROCESSED' | 'ERRORED';
}

export interface IGoogleReview {
    name: string;
    reviewer: {
        isAnonymous: boolean;
        displayName: string;
    };
    reviewReply?: {
        comment: string;
        updateTime: string;
    };
    starRating: 'STAR_RATING_UNSPECIFIED' | 'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE';
    comment: string;
    createTime: string;
}
